import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';
import Switch from 'react-bootstrap-switch';

import { Restaurant } from 'PMT/entities';
import { OrderSettings, DeliveryTimeSettings } from 'PMT/entities/order';

//
// components
//
import AbstractOrderSettingsForm from '../components/AbstractOrderSettingsForm';
import NumberOfDaysToProposeView from './components/NumberOfDaysToProposeView';
import PaymentModesView from './components/PaymentModesView';
import PaymentMethodsAllowed from './components/PaymentMethodsAllowed';

import DeliveryRangeMap from './components/maps/DeliveryRangeMap';

import FormGroupView from 'PMT/components/form/FormGroupView';

import NumberInput from 'PMT/components/form/NumberInput';
import PriceInput from 'PMT/components/form/PriceInput';
import TipTooltip from 'PMT/components/TipTooltip';

import DeliveryTimeSettingsView from './components/DeliveryTimeSettingsView';

import { Form } from 'react-bootstrap';

export default class DeliverySettingsPage extends AbstractOrderSettingsForm {

  static propTypes = {
    restaurant: PropTypes.instanceOf(Restaurant),

    orderSettings: PropTypes.instanceOf(OrderSettings),

    onSubmit: PropTypes.func.isRequired
  };

  state = this.getDefaultState(this.props.orderSettings);

  //
  //
  //

  onNumberOfDaysToProposeChange = (newValue: number) => {
    const orderSettings: OrderSettings = this.orderSettings;
    orderSettings.deliverySettings.setMaxTimeAheadWithDays(newValue);
    this.onChangeOrderSettings(orderSettings);
  };

  onMaxRangeInMetersChange = (maxRangeInMeters: number) => {
    if (_.isNumber(_.toNumber(maxRangeInMeters))) {
      const orderSettings: OrderSettings = this.orderSettings;
      orderSettings.deliverySettings.maxRangeInMeters = _.toNumber(maxRangeInMeters);
      this.onChangeOrderSettings(orderSettings);
    }
  };


  onMinimumPriceChange = (minimumPrice: number) => {
    const orderSettings: OrderSettings = this.orderSettings;
    orderSettings.deliverySettings.minimumPrice = minimumPrice;
    this.onChangeOrderSettings(orderSettings);
  };

  //
  //
  //

  onMaxRangeInMetersChangeOnMap = (newRange) => {
    console.log('onMaxRangeInMetersChangeOnMap', newRange);
  };

  onChangeOnSitePaymentModesSettings = (newValue: PaymentModes) => {
    const orderSettings: OrderSettings = this.orderSettings;
    orderSettings.deliverySettings.onSite = newValue;
    this.onChangeOrderSettings(orderSettings);
  };

  onItemsSequencedEnabledChange = () => {
    const orderSettings: OrderSettings = this.orderSettings;
    orderSettings.deliverySettings.itemsSequenced = !orderSettings.deliverySettings.itemsSequenced;
    this.onChangeOrderSettings(orderSettings);
  };

  //
  // Renderers
  //

  renderMinimumCart() {
    return (null);
  }

  renderNumberOfDaysToPropose() {
    return (
      <NumberOfDaysToProposeView
        numberOfDays={this.orderSettings.deliverySettings.getMaxTimeAheadAsDays()}
        onChange={this.onNumberOfDaysToProposeChange}
        readOnly={!this.canEditSettings()}
      />
    );
  }

  renderPaymentMethods() {
    return (
      <PaymentMethodsAllowed
        restaurant={this.props.restaurant}
        modeSettings={this.orderSettings.deliverySettings}
        onClick={(value) => this.onPaymentMethodsChange(value, this.orderSettings.deliverySettings)}
        disabled={!this.canEditSettings()}
      />
    );
  }

  renderPaymentsModes() {
    return (
      <PaymentModesView
        onSitePaymentModes={this.orderSettings.deliverySettings.onSite}
        onChange={this.onChangeOnSitePaymentModesSettings}
        readOnly={!this.canEditSettings()}
      />
    );
  }

  renderMaxRangeInMeters() {
    return (
      <FormGroupView
        name="max_range_in_meters"
      >
        <FormGroupView.Label>
          {I18n.tr('order.settings.delivery.max_range_in_meters')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <div className="input-group">
            <NumberInput
              value={this.orderSettings.deliverySettings.maxRangeInMeters}
              onChange={this.onMaxRangeInMetersChange}
              format={NumberInput.INT_FORMAT}
              min={1}
              max={50000} // 50km
              disabled={!this.canEditSettings()}
            />
            <div className="input-group-addon">
              <label>{I18n.tr('global.meters')}</label>
            </div>
          </div>
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderMinimumPrice() {
    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.delivery.minimum_price')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <PriceInput
            value={this.orderSettings.deliverySettings.minimumPrice}
            onChange={this.onMinimumPriceChange}
            currencyCode={this.props.restaurant.currency}
            readOnly={!this.canEditSettings()}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderDeliveryTime() {
    return (
      <DeliveryTimeSettingsView
        orderSettings={this.state.orderSettings}
        onChange={this.onChangeOrderSettings}
        readOnly={!this.canEditSettings()}
      />
    );
  }

  renderMaxRangeInMetersMap() {
    if (_.isNil(this.props.restaurant.geoPt)) {
      return (
        <p>{I18n.tr('order.settings.delivery.restaurant_address_not_defined')}</p>
      );
    }

    // TODO:
    // handle onRadiusChange by setting editable to true on editable.
    // Note: find a way to not let the user move the circle, juste the radius

    return (
      <DeliveryRangeMap
        maxRangeInMeters={this.orderSettings.deliverySettings.maxRangeInMeters}
        restaurantLocation={this.props.restaurant.geoPt}
        onRadiusChange={this.onMaxRangeInMetersChangeOnMap}
        readOnly={!this.canEditSettings()}
      />
    );
  }

  renderItemsSequenced() {
    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.itemsSequenced')}
          <TipTooltip
            text={I18n.tr('order.settings.itemsSequenced.tooltip')}
          />
        </FormGroupView.Label>
        <FormGroupView.Content>
          <Switch
            bsSize="mini"
            value={this.orderSettings.deliverySettings.itemsSequenced}
            onChange={this.onItemsSequencedEnabledChange}
            disabled={!this.canEditSettings()}
          />
        </FormGroupView.Content>
      </FormGroupView>
    )
  }

  renderBody() {
    return (
      <Form horizontal>
        {this.renderMinimumCart()}
        {this.renderNumberOfDaysToPropose()}
        {this.renderPaymentMethods()}

        {this.renderMinimumPrice()}

        {this.renderItemsSequenced()}

        {/* <hr /> */}

        {/* {this.renderPaymentsModes()} */}

        <hr />

        {this.renderDeliveryTime()}

        <hr />

        {this.renderMaxRangeInMeters()}
        {this.renderMaxRangeInMetersMap()}

        {this.renderFixedButtons()}
      </Form>
    );
  }

  render() {
    return (
      <div
        className="delivery_settings_page"
      >
        {this.renderBody()}
      </div>
    );
  }

}
